import { useState, useEffect, useRef } from 'react'
import styled, { keyframes, css } from 'styled-components'
import { supabase } from '../supabase'
import colors from '../utils/style/colors'
import { Helmet } from 'react-helmet-async'

// Deux consultations du même devis rapprochées ne comptent que pour une
const VIEW_THROTTLE_MS = 30 * 60 * 1000

const fadeUp = keyframes`from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}`
const bgShift = keyframes`from{filter:brightness(1)}to{filter:brightness(1.1)saturate(1.1)}`
const pulse = keyframes`0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.75;transform:scale(1.1)}`

// ── HERO ──
const MiniHero = styled.section`
  position: relative; height: 200px;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden; background: ${colors.heroBg};
`
const HeroBg = styled.div`
  position: absolute; inset: 0;
  background:
    radial-gradient(ellipse 80% 60% at 20% 60%, rgba(58,90,46,0.5) 0%, transparent 65%),
    radial-gradient(ellipse 70% 50% at 80% 40%, rgba(107,76,42,0.35) 0%, transparent 60%),
    linear-gradient(160deg, #0e1a0a 0%, #1e2e14 40%, #2a1a0a 100%);
  animation: ${bgShift} 10s ease-in-out infinite alternate;
`
const HeroGrain = styled.div`
  position: absolute; inset: 0; pointer-events: none; opacity: 0.05;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 200px;
`
const HeroContent = styled.div`
  position: relative; z-index: 2; text-align: center; animation: ${fadeUp} 0.8s both;
`
const HeroBadge = styled.span`
  display: inline-block; background: rgba(200,168,75,0.15);
  border: 1px solid rgba(200,168,75,0.4); color: ${colors.orClair};
  font-size: 0.7rem; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase;
  padding: 5px 14px; border-radius: 2px; margin-bottom: 14px; font-family: ${colors.font};
`
const HeroTitle = styled.h1`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(1.8rem, 4vw, 2.8rem); font-weight: 900; color: #fff;
  line-height: 1.1; margin: 0 0 8px;
  em { font-style: normal; color: ${colors.orClair}; }
`
const HeroSub = styled.p`
  font-family: ${colors.font}; font-size: 0.88rem; color: rgba(255,255,255,0.6); margin: 0;
`
const HeroBar = styled.div`
  position: absolute; bottom: 0; left: 0; right: 0; height: 4px;
  background: linear-gradient(90deg, transparent, ${colors.or}, transparent);
`

// ── PAGE ──
const PageWrap = styled.div`
  min-height: 65vh; background: ${colors.beige};
  display: flex; flex-direction: column; align-items: center;
  padding: 60px 24px 100px;
`
const SearchCard = styled.div`
  background: #faf8f4; border-radius: 20px;
  box-shadow: 0 24px 70px rgba(42,32,24,0.13);
  width: 100%; max-width: 460px;
  overflow: hidden; animation: ${fadeUp} 0.4s ease both;
  padding: 40px 36px 32px;
`
const ResultWrap = styled.div`
  width: 100%; max-width: 860px; animation: ${fadeUp} 0.4s ease both;
`
const FieldLabel = styled.label`
  font-family: ${colors.font}; font-size: 0.72rem; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase; color: ${colors.texteLeger};
  display: block; margin-bottom: 6px;
`
const Input = styled.input`
  border: 1.5px solid ${colors.beigeFonce}; border-radius: 8px;
  padding: 12px 14px; font-family: ${colors.font}; font-size: 0.95rem;
  color: ${colors.texte}; outline: none; width: 100%; background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus { border-color: ${colors.or}; box-shadow: 0 0 0 3px rgba(200,168,75,0.12); }
`
const Btn = styled.button`
  width: 100%; padding: 14px; background: ${colors.vertOlive}; color: #fff;
  border: none; border-radius: 8px; font-family: ${colors.font};
  font-size: 0.9rem; font-weight: 700; letter-spacing: 0.06em;
  cursor: pointer; margin-top: 8px; transition: all 0.2s;
  &:hover { background: #2a4520; transform: translateY(-1px); box-shadow: 0 6px 20px rgba(58,90,46,0.25); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`
const ErrorMsg = styled.p`
  color: #e05a5a; font-family: ${colors.font}; font-size: 0.82rem;
  text-align: center; margin: 10px 0 0;
`

// ── STEPPER HORIZONTAL ──
const STEPS = [
  { key: 'sent',        label: 'Devis envoyé',    icon: '📬', desc: 'Votre demande de devis a bien été envoyé. Nous allons la traiter dans les meilleurs délais.' },
  { key: 'received',    label: 'Demande reçue',   icon: '📤', desc: 'Nous avons bien pris connaissance de votre demande. Elle est en attente de traitement.' },
  { key: 'in_progress', label: 'Prise en charge', icon: '⚙️', desc: "Votre devis est en cours d'élaboration. Nos équipes préparent votre offre personnalisée." },
  { key: 'ready',       label: 'Devis prêt',      icon: '✅', desc: null }, // desc dynamique selon devis_content
]

const StepperCard = styled.div`
  background: #fff; border-radius: 16px;
  box-shadow: 0 8px 40px rgba(42,32,24,0.09);
  padding: 32px 36px 28px; margin-bottom: 20px;
  @media (max-width: 600px) { padding: 24px 18px 20px; }
`
const StepperSectionLabel = styled.div`
  font-family: ${colors.font}; font-size: 0.68rem; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase; color: ${colors.texteLeger};
  margin-bottom: 28px;
`
const StepperRow = styled.div`
  display: flex; align-items: flex-start; position: relative;
`
const TrackBg = styled.div`
  position: absolute; top: 22px; left: 24px; right: 24px; height: 3px;
  background: ${colors.beigeFonce}; z-index: 0; border-radius: 2px;
`
const TrackFill = styled.div`
  position: absolute; top: 22px; left: 24px; height: 3px;
  background: linear-gradient(90deg, ${colors.vertOlive}, ${colors.or});
  width: ${p => p.$pct}%;
  transition: width 1.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1; border-radius: 2px;
`
const StepCol = styled.div`
  flex: 1; display: flex; flex-direction: column;
  align-items: center; position: relative; z-index: 2;
`
const StepBubble = styled.div`
  width: 44px; height: 44px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: ${p => p.$done ? '1rem' : '1.25rem'};
  background: ${p => p.$done ? colors.vertOlive : p.$active ? colors.or : '#fff'};
  border: 3px solid ${p => p.$done ? colors.vertOlive : p.$active ? colors.or : colors.beigeFonce};
  color: ${p => p.$done ? '#fff' : 'inherit'};
  font-weight: ${p => p.$done ? 700 : 400};
  transition: all 0.4s;
  box-shadow: ${p => p.$active ? '0 0 0 7px rgba(200,168,75,0.15)' : p.$done ? '0 4px 12px rgba(58,90,46,0.2)' : 'none'};
  ${p => p.$active ? css`animation: ${pulse} 2.2s ease infinite;` : ''}
  margin-bottom: 10px;
`
const StepLbl = styled.div`
  font-family: ${colors.font}; font-size: 0.75rem;
  font-weight: ${p => p.$active ? 700 : 500};
  color: ${p => p.$done ? colors.vertOlive : p.$active ? colors.texte : colors.texteLeger};
  text-align: center; line-height: 1.35; transition: color 0.3s;
  @media (max-width: 480px) { font-size: 0.65rem; }
`
const ActiveBanner = styled.div`
  margin-top: 24px; padding: 14px 18px;
  background: linear-gradient(135deg, rgba(58,90,46,0.06), rgba(200,168,75,0.05));
  border: 1px solid rgba(58,90,46,0.14); border-radius: 10px;
  display: flex; align-items: flex-start; gap: 12px;
`
const ActiveBannerIcon = styled.div`font-size: 1.5rem; flex-shrink: 0; margin-top: 2px;`
const ActiveBannerText = styled.div`
  font-family: ${colors.font}; font-size: 0.85rem; color: ${colors.texte}; line-height: 1.65;
  strong { color: ${colors.vertOlive}; display: block; margin-bottom: 2px; }
`

// ── INFOS DEMANDE ──
const InfoGrid = styled.div`
  background: #fff; border-radius: 16px;
  box-shadow: 0 8px 40px rgba(42,32,24,0.09);
  padding: 24px 28px; margin-bottom: 20px;
  display: grid; grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 20px;
  @media (max-width: 640px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 380px) { grid-template-columns: 1fr; }
`
const InfoItem = styled.div``
const InfoLbl = styled.div`
  font-family: ${colors.font}; font-size: 0.67rem; font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase; color: ${colors.texteLeger}; margin-bottom: 4px;
`
const InfoVal = styled.div`
  font-family: ${colors.font}; font-size: 0.88rem; font-weight: 600; color: ${colors.texte};
`

// ── DEVIS ──
const DevisCard = styled.div`
  background: #fff; border-radius: 16px;
  box-shadow: 0 8px 40px rgba(42,32,24,0.09);
  overflow: hidden; margin-bottom: 24px;
`
const DevisHeader = styled.div`
  background: linear-gradient(135deg, ${colors.vertOlive} 0%, #1e3a16 100%);
  padding: 22px 32px; display: flex; align-items: center; gap: 16px;
`
const DevisHeaderIcon = styled.div`font-size: 2rem;`
const DevisHeaderTitle = styled.div`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.15rem; font-weight: 700; color: #fff; margin-bottom: 3px;
`
const DevisHeaderSub = styled.div`
  font-family: ${colors.font}; font-size: 0.78rem; color: rgba(255,255,255,0.6);
`
const DevisBody = styled.pre`
  font-family: ${colors.font}; font-size: 0.88rem; color: ${colors.texte};
  padding: 32px 36px; margin: 0; line-height: 1.85; white-space: pre-wrap;
  border-top: 1px solid ${colors.beigeFonce};
  @media (max-width: 600px) { padding: 22px 20px; font-size: 0.82rem; }
`
const DevisFooter = styled.div`
  background: ${colors.beige}; padding: 14px 32px;
  font-family: ${colors.font}; font-size: 0.75rem; color: ${colors.texteLeger};
  border-top: 1px solid ${colors.beigeFonce};
`

const BackBtn = styled.button`
  background: transparent; border: 1.5px solid ${colors.beigeFonce}; cursor: pointer;
  font-family: ${colors.font}; font-size: 0.82rem; color: ${colors.texteLeger};
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 18px; border-radius: 8px; transition: all 0.2s;
  &:hover { border-color: ${colors.or}; color: ${colors.texte}; background: #fff; }
`

// ── VALIDATION DEVIS ──
function ValidationBlock({ quote }) {
  const [showModal, setShowModal] = useState(false)
  const [validating, setValidating] = useState(false)
  const [done, setDone] = useState(false)

  const valider = async () => {
    setValidating(true)
    const now = new Date().toISOString()
    // Mettre à jour la commande liée
    await supabase.from('orders').update({ status: 'validated', validated_at: now }).eq('quote_id', quote.id)
    // Mettre à jour le quote aussi pour affichage
    await supabase.from('quotes').update({ validated_at: now }).eq('id', quote.id)
    // Passer le client en fidèle
    const { data: order } = await supabase.from('orders').select('client_id').eq('quote_id', quote.id).single()
    if (order?.client_id) {
      await supabase.from('clients').update({ type: 'fidele' }).eq('id', order.client_id)
    }
    setDone(true)
    setValidating(false)
    setShowModal(false)
  }

  if (done) return null

  return (
    <>
      <div style={{
        background: 'linear-gradient(135deg, rgba(200,168,75,0.08), rgba(58,90,46,0.06))',
        border: `1.5px solid ${colors.or}`, borderRadius: 14,
        padding: '20px 24px', marginBottom: 20, textAlign: 'center',
      }}>
        <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>🤝</div>
        <div style={{ fontFamily: 'Playfair Display', fontSize: '1.1rem', color: colors.texte, marginBottom: 6 }}>
          Cette offre vous convient ?
        </div>
        <div style={{ fontFamily: colors.font, fontSize: '0.84rem', color: colors.texteLeger, marginBottom: 16, lineHeight: 1.6 }}>
          Validez votre devis en ligne pour confirmer votre intérêt. Francky vous recontactera pour convenir ensemble de la date de livraison.
        </div>
        <button onClick={() => { setShowModal(true); window.scrollTo({ top: 0, behavior: 'smooth' }) }} style={{
          background: colors.vertOlive, color: '#fff', border: 'none',
          borderRadius: 8, padding: '12px 28px', fontFamily: colors.font,
          fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
          transition: 'all 0.2s', letterSpacing: '0.04em',
        }}
          onMouseEnter={e => { e.target.style.background = '#2a4520'; e.target.style.transform = 'translateY(-1px)' }}
          onMouseLeave={e => { e.target.style.background = colors.vertOlive; e.target.style.transform = 'none' }}
        >
          ✅ Valider ce devis
        </button>
      </div>

      {/* Modale de confirmation */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 20px 20px',
        }} onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '36px 32px',
            maxWidth: 460, width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,0.2)',
            fontFamily: colors.font,
          }}>
            <div style={{ fontSize: '2rem', textAlign: 'center', marginBottom: 12 }}>🤝</div>
            <h3 style={{ fontFamily: 'Playfair Display', color: colors.texte, margin: '0 0 12px', textAlign: 'center', fontSize: '1.3rem' }}>
              Confirmer la validation
            </h3>
            <p style={{ color: colors.texteLeger, fontSize: '0.88rem', lineHeight: 1.7, margin: '0 0 24px', textAlign: 'center' }}>
              En validant ce devis, vous confirmez votre intérêt pour cette offre.
              <br /><strong style={{ color: colors.texte }}>La marchandise sera réservée</strong> le temps que Francky vous recontacte pour convenir ensemble de la date et des modalités de livraison.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowModal(false)} style={{
                flex: 1, padding: '12px', background: 'transparent', color: colors.texteLeger,
                border: `1.5px solid ${colors.beigeFonce}`, borderRadius: 8,
                fontFamily: colors.font, fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer',
              }}>
                Annuler
              </button>
              <button onClick={valider} disabled={validating} style={{
                flex: 1, padding: '12px', background: colors.vertOlive, color: '#fff',
                border: 'none', borderRadius: 8, fontFamily: colors.font,
                fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer', opacity: validating ? 0.7 : 1,
              }}>
                {validating ? 'Validation…' : '✅ Oui, je valide'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ── RÉSULTAT ──
function SuiviResult({ quote, onBack }) {
  const currentIdx = STEPS.findIndex(s => s.key === quote.status)
  const pct = currentIdx === 0 ? 0 : Math.round((currentIdx / (STEPS.length - 1)) * 100)
  const active = STEPS[currentIdx]
  const activeDesc = active.key === 'ready'
    ? (quote.devis_content
        ? "Votre devis est finalisé et disponible ci-dessous. N'hésitez pas à nous contacter pour confirmer."
        : "Votre devis est prêt ! Nous finalisons son envoi et vous serez contacté très prochainement par email ou téléphone.")
    : active.desc

  return (
    <ResultWrap>
      {/* Stepper */}
      <StepperCard>
        <StepperSectionLabel>Avancement de votre demande</StepperSectionLabel>
        <StepperRow>
          <TrackBg />
          <TrackFill $pct={pct} />
          {STEPS.map((s, i) => {
            const done = i < currentIdx
            const isActive = i === currentIdx
            return (
              <StepCol key={s.key}>
                <StepBubble $done={done} $active={isActive}>
                  {done ? '✓' : s.icon}
                </StepBubble>
                <StepLbl $done={done} $active={isActive}>{s.label}</StepLbl>
              </StepCol>
            )
          })}
        </StepperRow>
        <ActiveBanner>
          <ActiveBannerIcon>{active.icon}</ActiveBannerIcon>
          <ActiveBannerText>
            <strong>{active.label}</strong>
            {activeDesc}
          </ActiveBannerText>
        </ActiveBanner>
      </StepperCard>

      {/* Infos */}
      <InfoGrid>
        <InfoItem>
          <InfoLbl>Référence</InfoLbl>
          <InfoVal>#{quote.id.slice(0,8).toUpperCase()}</InfoVal>
        </InfoItem>
        <InfoItem>
          <InfoLbl>Nom</InfoLbl>
          <InfoVal>{quote.name}</InfoVal>
        </InfoItem>
        <InfoItem>
          <InfoLbl>Email</InfoLbl>
          <InfoVal style={{ fontSize: '0.8rem' }}>{quote.email}</InfoVal>
        </InfoItem>
        <InfoItem>
          <InfoLbl>Date de la demande</InfoLbl>
          <InfoVal>{new Date(quote.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</InfoVal>
        </InfoItem>
      </InfoGrid>

      {/* Devis disponible */}
      {quote.devis_content && (
        <DevisCard>
          <DevisHeader>
            <DevisHeaderIcon>📄</DevisHeaderIcon>
            <div>
              <DevisHeaderTitle>Votre offre de prix personnalisée</DevisHeaderTitle>
              <DevisHeaderSub>Devis indicatif — achatpaille.com</DevisHeaderSub>
            </div>
          </DevisHeader>
          <DevisBody>{quote.devis_content}</DevisBody>
          <DevisFooter>
            Offre valable sous quinzaine · TVA non applicable (export Suisse) · achatpaille.com
          </DevisFooter>
        </DevisCard>
      )}

      {/* Bouton validation devis */}
      {quote.status === 'ready' && quote.devis_content && !quote.validated_at && (
        <ValidationBlock quote={quote} />
      )}
      {quote.validated_at && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(58,90,46,0.06))',
          border: '1.5px solid rgba(34,197,94,0.3)', borderRadius: 14,
          padding: '18px 22px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{ fontSize: '1.8rem' }}>✅</div>
          <div>
            <div style={{ fontFamily: colors.font, fontWeight: 700, color: '#16a34a', marginBottom: 3 }}>Devis validé !</div>
            <div style={{ fontFamily: colors.font, fontSize: '0.82rem', color: colors.texteLeger }}>
              Validé le {new Date(quote.validated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })} — Francky vous contactera pour convenir de la livraison.
            </div>
          </div>
        </div>
      )}

      <BackBtn onClick={onBack}>← Nouvelle recherche</BackBtn>
    </ResultWrap>
  )
}

// ── PAGE PRINCIPALE ──
export default function SuiviDevis() {
  const [email, setEmail] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('email') || ''
  })
  const [loading, setLoading] = useState(false)
  const [quotes, setQuotes] = useState(null)
  const [error, setError] = useState('')
  const [selected, setSelected] = useState(null)
  // Dernière vue enregistrée par devis, pour ne pas gonfler le compteur
  // quand le client passe d'un onglet à l'autre.
  const lastViewRef = useRef({})

  // Auto-ouverture depuis l'URL : ?q=<id> (lien du mail de devis) ou ?email=<adresse>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const quoteParam = params.get('q')
    const emailParam = params.get('email')
    if (!quoteParam && !emailParam) return

    const autoSearch = async () => {
      setLoading(true)

      // Lien direct vers un devis précis : on l'ouvre sans demander l'email
      if (quoteParam) {
        const { data } = await supabase.from('quotes').select('*').eq('id', quoteParam).maybeSingle()
        if (data) {
          setLoading(false)
          setSelected(data)
          window.scrollTo({ top: 0, behavior: 'smooth' })
          return
        }
        // Devis introuvable : on retombe sur la recherche par email si elle est fournie
        if (!emailParam) {
          setLoading(false)
          setError('Ce lien de suivi n’est plus valide. Recherchez votre demande avec votre email.')
          return
        }
      }

      const { data } = await supabase.from('quotes').select('*').eq('email', emailParam.trim().toLowerCase()).order('created_at', { ascending: false })
      setLoading(false)
      if (!data || data.length === 0) {
        setError('Aucune demande trouvée pour cet email.')
      } else if (data.length === 1) {
        setSelected(data[0])
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        setQuotes(data)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
    autoSearch()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected) return
    const id = selected.id

    const channel = supabase
      .channel(`quote-${id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'quotes', filter: `id=eq.${id}` },
        (payload) => { setSelected(prev => ({ ...prev, ...payload.new })) })
      .subscribe()

    // Enregistrer la vue (le builder est lazy : il faut .then() pour déclencher la requête)
    const record = () => {
      const now = Date.now()
      const last = lastViewRef.current[id]
      if (last && now - last < VIEW_THROTTLE_MS) return
      lastViewRef.current[id] = now
      supabase.from('quote_views').insert([{ quote_id: id }])
        .then(({ error }) => { if (error) console.error('quote_views insert', error) })
    }
    record()

    // Onglet laissé ouvert puis rouvert plus tard : sans ça, la consultation
    // n'était jamais comptée car l'effet ne rejoue pas.
    const onVisible = () => { if (document.visibilityState === 'visible') record() }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)

    return () => {
      supabase.removeChannel(channel)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [selected?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const search = async (e) => {
    e.preventDefault()
    if (!email.trim()) return
    setLoading(true); setError(''); setQuotes(null); setSelected(null)
    const { data } = await supabase.from('quotes').select('*').eq('email', email.trim().toLowerCase()).order('created_at', { ascending: false })
    setLoading(false)
    if (!data || data.length === 0) {
      setError('Aucune demande trouvée pour cet email.')
    } else if (data.length === 1) {
      setSelected(data[0])
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      setQuotes(data)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const Hero = (
    <MiniHero>
      <HeroBg /><HeroGrain />
      <HeroContent>
        <HeroBadge>📋 Espace client</HeroBadge>
        <HeroTitle>Suivi de votre <em>devis</em></HeroTitle>
        <HeroSub>Consultez l'avancement de votre demande en temps réel</HeroSub>
      </HeroContent>
      <HeroBar />
    </MiniHero>
  )

  if (selected) return (
    <>
      <Helmet><title>Suivi devis — Achat Paille</title></Helmet>
      {Hero}
      <PageWrap>
        <SuiviResult quote={selected} onBack={() => { setSelected(null); setQuotes(null); window.scrollTo({ top: 0, behavior: 'smooth' }) }} />
      </PageWrap>
    </>
  )

  return (
    <>
      <Helmet><title>Suivi devis — Achat Paille</title></Helmet>
      {Hero}
      <PageWrap>
        <SearchCard>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ fontSize: '2.8rem', marginBottom: 12 }}>📋</div>
            <h2 style={{ fontFamily: 'Playfair Display', color: colors.texte, margin: '0 0 10px', fontSize: '1.5rem' }}>
              Consulter mon devis
            </h2>
            <p style={{ fontFamily: colors.font, color: colors.texteLeger, fontSize: '0.87rem', margin: 0, lineHeight: 1.65 }}>
              Entrez l'email utilisé lors de votre demande pour suivre son avancement.
            </p>
          </div>

          <form onSubmit={search}>
            <div style={{ marginBottom: 14 }}>
              <FieldLabel>Votre adresse email</FieldLabel>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="votre@email.ch" required />
            </div>
            {error && <ErrorMsg>⚠ {error}</ErrorMsg>}
            <Btn type="submit" disabled={loading}>
              {loading ? 'Recherche…' : 'Consulter mon devis →'}
            </Btn>
          </form>

          <p style={{ textAlign: 'center', fontFamily: colors.font, fontSize: '0.75rem', color: colors.texteLeger, marginTop: 20, lineHeight: 1.6 }}>
            Pas encore de demande ?{' '}
            <a href="/devis" style={{ color: colors.or, fontWeight: 600 }}>Demandez un devis gratuit →</a>
          </p>
        </SearchCard>

        {quotes && (
          <div style={{ width: '100%', maxWidth: 460, marginTop: 16 }}>
            <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 8px 40px rgba(42,32,24,0.09)', padding: '28px 28px' }}>
              <h3 style={{ fontFamily: 'Playfair Display', color: colors.texte, marginBottom: 16, fontSize: '1.1rem' }}>
                {quotes.length} demande(s) trouvée(s)
              </h3>
              {quotes.map(q => (
                <div key={q.id} onClick={() => { setSelected(q); window.scrollTo({ top: 0, behavior: 'smooth' }) }} style={{
                  background: colors.beige, border: `1.5px solid ${colors.beigeFonce}`,
                  borderRadius: 10, padding: '14px 18px', marginBottom: 10,
                  cursor: 'pointer', transition: 'all 0.2s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = colors.or; e.currentTarget.style.background = '#fff' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = colors.beigeFonce; e.currentTarget.style.background = colors.beige }}
                >
                  <div style={{ fontFamily: colors.font, fontWeight: 700, marginBottom: 4, color: colors.texte }}>
                    Demande #{q.id.slice(0,8).toUpperCase()}
                  </div>
                  <div style={{ fontFamily: colors.font, fontSize: '0.8rem', color: colors.texteLeger }}>
                    {new Date(q.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </div>
                </div>
              ))}
              <BackBtn onClick={() => { setQuotes(null); window.scrollTo({ top: 0, behavior: 'smooth' }) }} style={{ marginTop: 8 }}>← Nouvelle recherche</BackBtn>
            </div>
          </div>
        )}
      </PageWrap>
    </>
  )
}
